<template>
  <div class="pt-4 pb-24 px-4">
    <h3 class="text-3xl font-bold mb-5">{{ $t('signals') }}</h3>

    <!-- Автоторговля -->
    <div class="rounded-lg mb-5 border" :class="[$theme.cardBg, $theme.cardBorderColor]">
      <div class="px-3">
        <SwitchGroup as="div" :class="['flex items-center justify-between py-2']">
          <span class="flex flex-grow flex-col">
            <SwitchLabel
                as="span"
                class="flex items-center justify-between text-sm font-medium leading-6"
                :class="[$theme.baseTextColor]"
                passive
            >
              <div class="flex items-center">
                <span class="ml-1">{{ $t?.('auto_trade') }}</span>
              </div>
            </SwitchLabel>
          </span>

          <Switch
              :disabled="isAutoProcessing"
              @click.prevent="toggleAutoTrade"
              :class="[
              isAutoTrade ? $theme.switchBgActive : $theme.switchBgInactive,
              isAutoProcessing ? 'opacity-50 pointer-events-none' : '',
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2',
              $theme.switchRingFocus
            ]"
          >
            <span
                aria-hidden="true"
                :class="[
                isAutoTrade ? 'translate-x-5' : 'translate-x-0',
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
              ]"
            />
          </Switch>
        </SwitchGroup>
      </div>
    </div>

    <!-- Тип актива -->
    <div class="block">
      <nav class="flex space-x-4 mb-5" aria-label="Tabs">
        <a
            v-for="tab in filtersByTypeAsset"
            :key="tab.key"
            href="javascript:void(0)"
            @click="onChangeTypeAsset(tab.key)"
            :class="[
            tab.key === filters.activeTypeAsset
              ? `${$theme.activeTabBorder} ${$theme.activeTabTextColor}`
              : `${$theme.tabTextColor} border-transparent`,
            'rounded-lg px-3 py-2 text-sm font-medium border'
          ]"
        >
          {{ tab.name }}
        </a>
      </nav>
    </div>

    <div v-if="loadingSignals" class="rounded-xl overflow-hidden mt-4" :class="[$theme.cardBg]">
      <LoadingBlock />
    </div>

    <template v-else>
      <template v-if="signals.length">
        <!-- ВИРТУАЛИЗАЦИЯ -->
        <DynamicScroller
            class="signals-scroller"
            :items="signals"
            key-field="id"
            :min-item-size="120"
            :buffer="400"
            page-mode
            v-slot="{ item: signal, index, active }"
        >
          <DynamicScrollerItem
              :item="signal"
              :active="active"
              :data-index="index"
              :size-dependencies="[signal.locked, signal.name]"
          >
            <!-- ВАЖНО: вместо mb-2 используем padding, чтобы размер учитывался корректно -->
            <div class="pb-2">
              <SignalItemNew
                  :signal="signal"
                  :theme="$theme"
                  :is-enabled="signalEnabledSet.has(Number(signal.id))"
                  :capitalize-first-letter="capitalizeFirstLetter"
                  :isLast="index === signals.length - 1"
                  :selected-exchanges="signalExchanges[String(signal.id)] || []"
                  :exchange-id-to-slug="exchangeIdToSlug"
                  @toggle="toggleSignal"
                  @error="onSignalError"
              />
            </div>
          </DynamicScrollerItem>
        </DynamicScroller>
      </template>

      <template v-else>
        <div class="mt-5 rounded-xl overflow-hidden px-4 py-4">
          <NotSearchIcon class="mx-auto" />
          <p class="text-center opacity-60 mt-5">
            {{ $t('no_signals_for_selected_asset') }}
          </p>
        </div>
      </template>
    </template>

    <!-- Модалка подтверждения все вкл/выкл -->
    <TransitionRoot as="template" :show="openConfirmDisableTrade">
      <Dialog class="relative z-50" @close="cancelToggleAllSignals">
        <div class="fixed inset-0 bg-gray-950 bg-opacity-45 transition-opacity" />
        <div class="fixed inset-0 z-50 flex items-end justify-center">
          <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-300"
              enter-from="translate-y-full opacity-0"
              enter-to="translate-y-0 opacity-100"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-y-0 opacity-100"
              leave-to="translate-y-full opacity-0"
          >
            <DialogPanel
                class="w-full rounded-t-2xl px-4 pt-5 pb-4 text-left shadow-orange sm:p-6"
                :class="[$theme.baseBg, $theme.baseTextColor]"
            >
              <div class="mt-3 text-center">
                <h3 class="text-lg leading-6 font-medium">
                  {{ confirmDisableAll ? $t('confirm_disable_all_signals') : $t('confirm_enable_all_signals') }}
                </h3>

                <div class="mt-1">
                  <p class="text-sm">
                    {{
                      confirmDisableAll
                          ? $t('are_you_sure_you_want_to_disable_all_signals')
                          : ($t('are_you_sure_you_want_to_enable_all_signals') + ' *')
                    }}
                  </p>

                  <p v-if="!confirmDisableAll" class="text-xs opacity-70 mt-1">
                    {{
                      $t('bulk_enable_note') ||
                      'При массовом включении будут использованы сохранённые биржи по сигналам. Для выбора — включайте по одному.'
                    }}
                  </p>
                </div>
              </div>

              <div class="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                <button
                    @click="confirmToggleAllSignals"
                    type="button"
                    :disabled="isBulkProcessing"
                    class="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 text-base font-medium"
                    :class="[
                    $theme.bgBtn,
                    $theme.whiteText,
                    isBulkProcessing ? 'opacity-60 pointer-events-none' : ''
                  ]"
                >
                  {{ isBulkProcessing ? $t('please_wait') : $t('confirm') }}
                </button>

                <button
                    @click="cancelToggleAllSignals"
                    type="button"
                    :disabled="isBulkProcessing"
                    class="flex w-full items-center justify-center rounded-xl px-3 py-3 mt-4 font-semibold"
                    :class="[
                    $theme.placeBg,
                    $theme.cardLabelTextColor,
                    isBulkProcessing ? 'opacity-60 pointer-events-none' : ''
                  ]"
                >
                  {{ $t('cancel') }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Модалка "Недостаточно средств" -->
    <TransitionRoot as="template" :show="showInsufficient">
      <Dialog class="relative z-50" @close="closeInsufficient">
        <div class="fixed inset-0 bg-gray-950 bg-opacity-45 transition-opacity" />
        <div class="fixed inset-0 z-50 flex items-end justify-center">
          <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-300"
              enter-from="translate-y-full opacity-0"
              enter-to="translate-y-0 opacity-100"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-y-0 opacity-100"
              leave-to="translate-y-full opacity-0"
          >
            <DialogPanel
                class="w-full rounded-t-2xl px-4 pt-5 pb-8 text-left shadow-orange sm:p-6"
                :class="[$theme.baseBg, $theme.baseTextColor]"
            >
              <div class="text-center">
                <h3 class="text-lg leading-6 font-medium">
                  {{ $t?.('not_enough_balance_title') || 'Недостаточно средств' }}
                </h3>

                <div class="mt-2 text-sm opacity-90">
                  <p v-if="insufficient.signal_name" class="font-medium">
                    {{ insufficient.signal_name }}
                  </p>

                  <p class="mt-2">
                    {{ $t('cant_turn_signal') }}
                    <strong>{{ fmt(insufficient.required_balance) }}</strong>
                    <span v-if="insufficient.currency">{{ insufficient.currency }}</span>.
                  </p>

                  <p class="mt-1">
                    {{ $t('total_reserve_will') }}
                    <strong>{{ fmt(insufficient.reserved_balance) }}</strong>
                    <span v-if="insufficient.currency">{{ insufficient.currency }}</span>.
                  </p>

                  <p class="mt-3 opacity-80">
                    {{ $t('up_balance_some_note') }}
                  </p>
                </div>
              </div>

              <div class="mt-5 sm:mt-6">
                <button
                    type="button"
                    class="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 text-base font-medium"
                    :class="[$theme.bgBtn, $theme.whiteText]"
                    @click="closeInsufficient"
                >
                  {{ $t?.('ok') || 'Понятно' }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
import NotSearchIcon from "@/components/icons/NotSearchIcon.vue";
import SignalItemNew from "@/components/elem/SignalItemNew.vue";
import { Switch, SwitchGroup, SwitchLabel, Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { addBackButton, hideBackButton } from "@/helpers/telegramHelper";
import { capitalizeFirstLetter } from "@/helpers/stringHelper";
import LoadingBlock from "@/components/LoadingBlock.vue";

import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";

export default {
  components: {
    LoadingBlock,
    SignalItemNew,

    Switch,
    SwitchGroup,
    SwitchLabel,

    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,

    NotSearchIcon,

    DynamicScroller,
    DynamicScrollerItem,
  },

  data() {
    return {
      loadingSignals: true,
      loadingUser: true,

      // ВАЖНО: храним числа, чтобы includes() работал стабильно
      signalEnabled: [],        // [signalId,...]
      signalExchanges: {},      // { [signalId]: [exchangeId,...] }

      signals: [],
      error: null,

      // 1 раз грузим подключённые биржи: id -> slug
      exchangeIdToSlug: {},

      openConfirmDisableTrade: false,
      confirmDisableAll: false,
      isBulkProcessing: false,

      showInsufficient: false,
      insufficient: {
        message: "",
        required_balance: null,
        reserved_balance: null,
        currency: null,
        signal_id: null,
        signal_name: null,
      },

      capitalizeFirstLetter,
      user: null,

      filtersByTypeAsset: [
        { name: "USDT", key: "USDT" },
        { name: "USDC", key: "USDC" },
      ],
      filters: { activeTypeAsset: "USDT" },

      isAutoTrade: false,
      isAutoProcessing: false,
    };
  },

  computed: {
    isReady() {
      return !this.loadingSignals && !this.loadingUser;
    },

    signalEnabledSet() {
      return new Set((this.signalEnabled || []).map(Number));
    },

    allowedSignalsIds() {
      if (this.user?.tariff?.signals?.length > 0) {
        return this.user.tariff.signals.map((s) => Number(s.id));
      }
      return (this.signals || []).map((s) => Number(s.id));
    },

    isAllSignalsDisabled() {
      const enabled = this.signalEnabledSet;
      return this.allowedSignalsIds.every((id) => !enabled.has(Number(id)));
    },
  },

  methods: {
    normalizeSignalExchanges(raw) {
      if (!raw) return {};
      if (Array.isArray(raw)) {
        return Object.fromEntries(
            raw.map((r) => [String(r.signal_id), (r.exchange_ids || []).map(Number)])
        );
      }
      return Object.fromEntries(
          Object.entries(raw).map(([k, v]) => [String(k), (v || []).map(Number)])
      );
    },

    async loadConnectedExchanges() {
      try {
        const { data } = await this.$api.get("/exchanges/connected");
        const list = data?.data ?? data ?? [];
        this.exchangeIdToSlug = Object.fromEntries(
            list.map((e) => [Number(e.id), e.exchange_slug])
        );
      } catch (e) {
        this.exchangeIdToSlug = {};
      }
    },

    async toggleAutoTrade() {
      if (this.isAutoProcessing) return;
      const next = !this.isAutoTrade;

      this.isAutoProcessing = true;
      try {
        await this.$api.post("/user/update-auto-trade", { is_auto_trade: Number(next) });
        this.isAutoTrade = next;

        if (this.user) {
          this.user.is_auto_trade = next;
          this.$.appContext.config.globalProperties.$user = this.user;
        }
      } catch (e) {
        this.error = e?.response?.data?.message || (this.$t?.("something_went_wrong") || "Что-то пошло не так");
      } finally {
        this.isAutoProcessing = false;
      }
    },

    fmt(n) {
      if (n === null || n === undefined || n === "") return "-";
      return Number(n).toLocaleString(undefined, { maximumFractionDigits: 8 });
    },

    onSignalError(payload) {
      const p = typeof payload === "string" ? { message: payload } : payload || {};
      this.insufficient = {
        message: p.message || "",
        required_balance: p.required_balance ?? null,
        reserved_balance: p.reserved_balance ?? null,
        currency: p.currency ?? null,
        signal_id: p.signal_id ?? null,
        signal_name: p.signal_name ?? null,
      };
      this.showInsufficient = true;
    },

    closeInsufficient() {
      this.showInsufficient = false;
    },

    toggleDisableAllSignals(event) {
      event.preventDefault();
      this.confirmDisableAll = !this.isAllSignalsDisabled;
      this.openConfirmDisableTrade = true;
    },

    async confirmToggleAllSignals() {
      if (this.isBulkProcessing) return;
      this.isBulkProcessing = true;

      try {
        if (this.confirmDisableAll) {
          this.signalEnabled = [];
          this.signalExchanges = {};
          await this.updateUserSettings();
        } else {
          const enabled = this.signalEnabledSet;
          const candidates = this.allowedSignalsIds.filter((id) => !enabled.has(Number(id)));

          for (const id of candidates) {
            const ex = this.signalExchanges[String(id)] || [];
            if (!ex.length) continue;

            const { ok } = await this.checkCanEnable(id, ex);
            if (ok) this.signalEnabled = [...new Set([...this.signalEnabled, Number(id)])];
          }

          await this.updateUserSettings();
        }
      } finally {
        this.isBulkProcessing = false;
        this.openConfirmDisableTrade = false;
      }
    },

    cancelToggleAllSignals() {
      if (this.isBulkProcessing) return;
      this.openConfirmDisableTrade = false;
    },

    async toggleSignal(payload) {
      const parsed =
          typeof payload === "object" && payload !== null
              ? { id: Number(payload.id), exchange_ids: (payload.exchange_ids || []).map(Number) }
              : { id: Number(payload), exchange_ids: [] };

      const id = parsed.id;
      const enabledSet = this.signalEnabledSet;
      const enabled = enabledSet.has(id);

      // выключение
      if (enabled) {
        this.signalEnabled = this.signalEnabled.filter((sid) => Number(sid) !== id);
        const nextEx = { ...this.signalExchanges };
        delete nextEx[String(id)];
        this.signalExchanges = nextEx;

        try {
          await this.updateUserSettings();
        } catch (e) {
          // откат
          this.signalEnabled = [...new Set([...this.signalEnabled, id])];
          this.signalExchanges = { ...this.signalExchanges, [String(id)]: parsed.exchange_ids || [] };
          this.onSignalError(this.$t?.("something_went_wrong") || "Something went wrong");
        }
        return;
      }

      // включение
      let selected = parsed.exchange_ids.length ? parsed.exchange_ids : (this.signalExchanges[String(id)] || []);
      if (!selected.length) {
        this.onSignalError(this.$t?.("no_exchanges_found") || "Нет доступных бирж");
        return;
      }

      const { ok, message, required_balance, reserved_balance, currency } = await this.checkCanEnable(id, selected);
      if (!ok) {
        const sig = this.signals.find((s) => Number(s.id) === id);
        this.onSignalError({
          message,
          required_balance,
          reserved_balance,
          currency,
          signal_id: id,
          signal_name: sig?.name,
        });
        return;
      }

      this.signalEnabled = [...new Set([...this.signalEnabled, id])];
      this.signalExchanges = { ...this.signalExchanges, [String(id)]: selected };

      try {
        await this.updateUserSettings();
      } catch (e) {
        this.signalEnabled = this.signalEnabled.filter((sid) => sid !== id);
        const nextEx = { ...this.signalExchanges };
        delete nextEx[String(id)];
        this.signalExchanges = nextEx;

        this.onSignalError(this.$t?.("something_went_wrong") || "Something went wrong");
      }
    },

    async checkCanEnable(signalId, exchangeIds) {
      try {
        const { data } = await this.$api.get(`/signals/${signalId}/check`, {
          params: { exchange_ids: exchangeIds },
        });

        if (data?.success) return { ok: true };

        const failing = (data?.checked || []).find((x) => x.ok === false);
        return {
          ok: false,
          message:
              failing?.reason === "NO_WALLET"
                  ? (this.$t?.("no_wallet") || "Нет кошелька")
                  : (this.$t?.("not_enough_balance_to_enable") || "Недостаточно баланса"),
          required_balance: failing?.required_balance,
          reserved_balance: failing?.reserved_balance,
          currency: data?.currency,
        };
      } catch (e) {
        return {
          ok: false,
          message: e?.response?.data?.message || (this.$t?.("something_went_wrong") || "Something went wrong"),
        };
      }
    },

    updateUserSettings() {
      const payload = {
        signals: (this.signalEnabled || []).map(Number),
        signal_exchanges: this.signalExchanges,
      };

      return this.$api
          .post("/user/update-settings", payload)
          .then((result) => {
            this.$store.commit("updateEnabledSignals", result.data.signals);
            this.loadUser();
            return result;
          })
          .catch((e) => {
            this.error = e;
            throw e;
          });
    },

    onChangeTypeAsset(type) {
      this.filters.activeTypeAsset = type;
      this.loadSignals();
    },

    loadSignals() {
      this.loadingSignals = true;
      return this.$api
          .get("/signals/by-user-tariff?type_asset=" + this.filters.activeTypeAsset)
          .then((result) => {
            this.signals = result.data.data || [];
          })
          .catch((e) => {
            this.error = e;
          })
          .finally(() => {
            this.loadingSignals = false;
          });
    },

    loadUser() {
      this.loadingUser = true;
      return this.$api
          .get("/user")
          .then((result) => {
            this.user = result.data.data;

            this.signalEnabled = (this.user?.settings?.signals || []).map(Number);
            this.isAutoTrade = !!this.user?.is_auto_trade;

            this.signalExchanges = this.normalizeSignalExchanges(this.user?.settings?.signal_exchanges);

            this.$.appContext.config.globalProperties.$user = this.user;
          })
          .catch((e) => {
            this.error = e;
          })
          .finally(() => {
            this.loadingUser = false;
          });
    },
  },

  async mounted() {
    addBackButton(this.$router);

    await Promise.all([
      this.loadUser(),
      this.loadSignals(),
      this.loadConnectedExchanges(),
    ]);
  },

  beforeUnmount() {
    hideBackButton();
  },
};
</script>
